import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { format, subDays, addDays } from 'date-fns';
import {
  Factory,
  Save,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Package
} from 'lucide-react';

const ProductionPage = () => {
  const { API_URL, getAuthHeader } = useAuth();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar' || language === 'ku';

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [products, setProducts] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const dateStr = format(selectedDate, 'yyyy-MM-dd');

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [productsRes, productionRes] = await Promise.all([
        axios.get(`${API_URL}/api/products`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/production/${dateStr}`, { headers: getAuthHeader() })
      ]);

      setProducts(productsRes.data);

      // Map existing production data
      const existingQty = {};
      productionRes.data.items?.forEach(item => {
        existingQty[item.product_id] = item.quantity;
      });
      // Initialize all products with 0 or existing value
      const initQty = {};
      productsRes.data.forEach(p => {
        initQty[p.id] = existingQty[p.id] || 0;
      });
      setQuantities(initQty);
      setNotes(productionRes.data.notes || '');
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('loadError') || 'خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const items = Object.entries(quantities)
        .filter(([_, qty]) => qty > 0)
        .map(([product_id, quantity]) => ({ product_id, quantity: parseFloat(quantity) }));

      await axios.post(`${API_URL}/api/production`, {
        date: dateStr,
        items,
        notes
      }, { headers: getAuthHeader() });

      toast.success(t('productionSaved'));
    } catch (error) {
      console.error('Error saving production:', error);
      toast.error(t('error') || 'حدث خطأ');
    } finally {
      setSaving(false);
    }
  };

  const updateQuantity = (productId, value) => {
    setQuantities(prev => ({
      ...prev,
      [productId]: value === '' ? '' : parseFloat(value) || 0
    }));
  };

  const totalProduced = Object.values(quantities).reduce((sum, q) => sum + (parseFloat(q) || 0), 0);

  const formattedDate = format(selectedDate, 'yyyy/MM/dd');

  return (
    <div data-testid="production-page" className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-stone-900">
            <Factory className="inline w-7 h-7 me-2 text-amber-600" />
            {t('dailyProduction')}
          </h1>
          <p className="text-stone-500 mt-1">{t('productionQuantity')}</p>
        </div>
      </div>

      {/* Date Selector */}
      <Card className="border-stone-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedDate(prev => subDays(prev, 1))}
              className="h-10 w-10 p-0"
            >
              {isRTL ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </Button>
            <div className="text-center">
              <Input
                type="date"
                value={dateStr}
                onChange={(e) => e.target.value && setSelectedDate(new Date(e.target.value + 'T12:00:00'))}
                className="text-center font-bold text-lg border-amber-300 focus:border-amber-500"
                data-testid="production-date-input"
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedDate(prev => addDays(prev, 1))}
              className="h-10 w-10 p-0"
            >
              {isRTL ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Production Form */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full"></div>
        </div>
      ) : products.length === 0 ? (
        <Card className="border-stone-200">
          <CardContent className="p-8 text-center">
            <Package className="w-12 h-12 mx-auto text-stone-300 mb-3" />
            <p className="text-stone-500">{t('noProducts')}</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-stone-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-600" />
              {t('produced')} - {formattedDate}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map(product => (
                <div
                  key={product.id}
                  className="flex items-center gap-3 p-4 rounded-xl border-2 border-stone-200 bg-stone-50 hover:border-amber-300 transition-colors"
                >
                  <div className="flex-1">
                    <p className="font-bold text-stone-800">{product.name}</p>
                    <p className="text-xs text-stone-500">{product.breads_per_box} / {t('box') || 'صندوق'}</p>
                  </div>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={quantities[product.id] ?? 0}
                    onChange={(e) => updateQuantity(product.id, e.target.value)}
                    className="w-24 text-center text-lg font-bold border-amber-300 focus:border-amber-500"
                    data-testid={`production-qty-${product.id}`}
                  />
                </div>
              ))}
            </div>

            {/* Total */}
            <div className="flex items-center justify-between p-4 bg-amber-50 border-2 border-amber-200 rounded-xl">
              <span className="font-bold text-amber-800 text-lg">{t('total')}</span>
              <span className="text-2xl font-bold text-amber-700" data-testid="production-total">
                {totalProduced}
              </span>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-stone-700">{t('notes') || 'ملاحظات'}</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('optionalNotes') || 'ملاحظات اختيارية...'}
                className="resize-none"
                rows={2}
                data-testid="production-notes"
              />
            </div>

            {/* Save Button */}
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-3 text-lg"
              data-testid="save-production-btn"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin me-2" />
              ) : (
                <Save className="w-5 h-5 me-2" />
              )}
              {t('saveProduction')}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProductionPage;
